import { type UserRecord, type UserRecordUpdate } from '@lg/shared'
import { AuthService } from '../services/auth-service.js'
import { AppContext } from '../app-context.js'
import { type PagingDefinition, type User } from '@lg/shared'
import { firstValueFrom, tap } from 'rxjs'
import { IdbUserProperty, SessionCookieName } from '@lg/shared/for-apps'
import { AppTrpcServiceInjectionToken } from '../services/app-trpc.service.js'
import { IdbService } from '../services/idb.service.js'

export class FrontendUserBridge {
	static get currentUser() {
		return AppContext.resolve(AuthService).getUser()
	}

	static get sessionId() {
		const cookies = document.cookie.split(/;\s*/)
		const sessionCookie = cookies.find((c) =>
			c.startsWith(`${SessionCookieName}=`),
		)
		const parts = sessionCookie?.split('=') ?? []
		return parts[1]
	}

	static async updatePassword(oldPassword: string, newPassword: string) {
		const authService = AppContext.resolve(AuthService)
		return authService.updatePassword(oldPassword, newPassword)
	}

	static async isUsernameAvailable(userName: string) {
		const authService = AppContext.resolve(AuthService)
		return authService.isUsernameAvailable(userName)
	}

	static async registerUser(
		user: User,
		password: string,
		generatePassword: boolean,
	) {
		const authService = AppContext.resolve(AuthService)
		return authService.registerUser(user, password, generatePassword)
	}

	static async updateUser(user: UserRecordUpdate) {
		const trpc = AppContext.resolve(AppTrpcServiceInjectionToken)
		const updatedUser = await trpc.client.user.updateUserRecord.mutate({ user })
		if (updatedUser) {
			const idb = new IdbService()
			await idb.put(IdbUserProperty, updatedUser)
		}
		return updatedUser
	}

	static async searchUser(query: string, paging?: PagingDefinition) {
		const trpc = AppContext.resolve(AppTrpcServiceInjectionToken)
		const result = await trpc.client.user.searchUser.query({ query, paging })
		return result
	}

	static async login(userName: string, password: string, mfaToken?: string) {
		const authService = AppContext.resolve(AuthService)
		const login$ = authService.login$({
			stage: mfaToken ? 'mfa' : 'credentials',
			userName,
			password,
			mfaToken,
		})
		return firstValueFrom(login$)
	}

	static async logout() {
		const authService = AppContext.resolve(AuthService)
		const logout$ = authService.logout$()
		return firstValueFrom(logout$)
	}

	static async forceRenewLogin() {
		const authService = AppContext.resolve(AuthService)
		return authService.forceRenewLogin()
	}
}
